import { Body, Stack } from '@qred/components-library';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import { DocumentType } from '~/constants/fileUpload';
import { GlobalErrorType } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { IFile } from '~/interfaces/shared';
import { getSignedUrl, postFile } from '~/services/api';
import { RootState } from '~/store';
import { useCardOfferSelectors } from '~/store/selectors/cardOffer.selector';
import { toggleGlobalErrorOn } from '~/store/slices/globalError.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import { useGoToNextStepCardOffer } from '../../hooks/useGoToNextStepCardOffer';

const OwnershipFooter = () => {
  const dispatch = useAppDispatch();
  const {
    cardOffer: {
      form: { ownershipFiles, clientId },
    },
    intl: { market },
  } = useSelector((state: RootState) => state);
  const translate = useTranslate();

  const onNextStepClick = useGoToNextStepCardOffer();
  const { ownersDataIsFetched } = useCardOfferSelectors();

  const [documentsApiStatus, setDocumentsApiStatus] = useState(ApiStatus.Idle);

  const createFilesFromObjectUrls = (documents: IFile[]) =>
    Promise.all(
      documents.map(async (document) => {
        const res = await fetch(document.objectURL);
        const blob = await res.blob();
        return new File([blob], document.name, blob);
      })
    );

  const postFilesToS3 = async (files: File[]) => {
    const fileKeys = ownershipFiles.map((localFile) => localFile.name);
    const res = await getSignedUrl(
      fileKeys,
      clientId ? String(clientId) : '',
      market,
      DocumentType.Ownership
    );
    await Promise.all(
      res.data.map(async (infoContainer: any, i: number) => {
        const form = new FormData();
        Object.keys(infoContainer.fields).forEach((key) =>
          form.append(key, infoContainer.fields[key])
        );
        form.append('file', files[i]);
        await postFile(infoContainer.url, form);
      })
    );
    return res.data.map((file) => file.fields.key);
  };

  const uploadFilesToS3 = async () => {
    const files = await createFilesFromObjectUrls(ownershipFiles);
    const filePaths = await postFilesToS3(files);
    return filePaths;
  };

  const uploadDocuments = async () => {
    setDocumentsApiStatus(ApiStatus.Started);
    try {
      await uploadFilesToS3();
      setDocumentsApiStatus(ApiStatus.Completed);
      onNextStepClick();
    } catch (err: any) {
      setDocumentsApiStatus(ApiStatus.Failed);
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
      logToSentry(err, 'uploadOwnershipDocuments', {});
    }
  };

  return (
    <Stack spacing={'sm'} align="center">
      {ownersDataIsFetched && (
        <Body size="sm">
          {translate('OnboardingOffer.BeneficialOwnerNextButtonConfirmation')}
        </Body>
      )}
      {ownershipFiles.length ? (
        <NextButton
          label="OnboardingOffer.Next"
          isLoading={documentsApiStatus === ApiStatus.Started}
          action={uploadDocuments}
        />
      ) : (
        <NextButton label="OnboardingOffer.Next" action={onNextStepClick} />
      )}
    </Stack>
  );
};

export default OwnershipFooter;
